import TapGame from '../microgames/TapGame.js';
import SwipeGame from '../microgames/SwipeGame.js';
import DontMoveGame from '../microgames/DontMoveGame.js';
import MazeGame from '../microgames/MazeGame.js';
import SoccerGame from '../microgames/SoccerGame.js';
import HoldGame from '../microgames/HoldGame.js';
import ShakeGame from '../microgames/ShakeGame.js';
import ChargeGame from '../microgames/ChargeGame.js';
import DodgeGame from '../microgames/DodgeGame.js';
import MatchGame from '../microgames/MatchGame.js';
import TypeGame from '../microgames/TypeGame.js';
import SpinGame from '../microgames/SpinGame.js';
import CatchGame from '../microgames/CatchGame.js';
import CountGame from '../microgames/CountGame.js';
import FindGame from '../microgames/FindGame.js';
import ConnectGame from '../microgames/ConnectGame.js';
import PumpGame from '../microgames/PumpGame.js';
import SliceGame from '../microgames/SliceGame.js';
import UnlockGame from '../microgames/UnlockGame.js';
import SortGame from '../microgames/SortGame.js';
import StopwatchGame from '../microgames/StopwatchGame.js';
import WhackGame from '../microgames/WhackGame.js';
import DefendGame from '../microgames/DefendGame.js';
import BalanceGame from '../microgames/BalanceGame.js';
import AlignGame from '../microgames/AlignGame.js';
import SafeCrackGame from '../microgames/SafeCrackGame.js';
import OrbitGame from '../microgames/OrbitGame.js';
import RopeGame from '../microgames/RopeGame.js';
import ArcheryGame from '../microgames/ArcheryGame.js';
import KeepUpGame from '../microgames/KeepUpGame.js';
import FillGame from '../microgames/FillGame.js';
import MathGame from '../microgames/MathGame.js';
import SimonGame from '../microgames/SimonGame.js';
import BasketGame from '../microgames/BasketGame.js';
import StackGame from '../microgames/StackGame.js'; // Added
import JumpGame from '../microgames/JumpGame.js'; // Added (if needed later)
import GolfGame from '../microgames/GolfGame.js';

export default class ChallengeManager {
    constructor(engine) {
        this.engine = engine;
        this.progress = {}; // Map<challengeId, {stars, bestScore}>
        this.challenges = []; // Initialize empty, will load from DB or fallback
        this.initChallenges(); // Async init
    }

    async initChallenges() {
        const definitions = await this.fetchDefinitions();
        if (definitions && definitions.length > 0) {
            console.log(`[ChallengeManager] Loaded ${definitions.length} challenges from DB (Expected ~30)`);
            this.challenges = this.mapDefinitions(definitions);
        } else {
            console.error("[ChallengeManager] Failed to load challenges from DB. No challenges available.");
            this.challenges = [];
        }
        // Always fetch from server
        if (this.engine.authManager.token) {
            this.fetchProgress();
        }
    }

    async fetchDefinitions() {
        // Public endpoint, no token needed
        // Assuming AuthManager has the base URL even if not logged in?
        // ApiDatabase usually sets this up.

        let baseUrl = 'https://pixelblob.fr/api'; // Default
        if (this.engine && this.engine.authManager && this.engine.authManager.db) {
            baseUrl = this.engine.authManager.db.baseUrl;
        }

        try {
            const res = await fetch(`${baseUrl}/challenge/definitions`);
            if (res.ok) {
                return await res.json();
            }
        } catch (e) {
            console.warn("Failed to fetch challenge definitions:", e);
        }
        return null;
    }

    mapDefinitions(defs) {
        const classMap = {
            'TapGame': TapGame, 'SwipeGame': SwipeGame, 'DontMoveGame': DontMoveGame, 'MazeGame': MazeGame, 'SoccerGame': SoccerGame,
            'HoldGame': HoldGame, 'ChargeGame': ChargeGame, 'DodgeGame': DodgeGame, 'MatchGame': MatchGame, 'TypeGame': TypeGame,
            'SpinGame': SpinGame, 'CatchGame': CatchGame, 'CountGame': CountGame, 'FindGame': FindGame, 'ConnectGame': ConnectGame,
            'PumpGame': PumpGame, 'SliceGame': SliceGame, 'UnlockGame': UnlockGame, 'SortGame': SortGame, 'StopwatchGame': StopwatchGame,
            'WhackGame': WhackGame, 'DefendGame': DefendGame, 'BalanceGame': BalanceGame, 'AlignGame': AlignGame,
            'SafeCrackGame': SafeCrackGame, 'OrbitGame': OrbitGame, 'RopeGame': RopeGame,
            'ArcheryGame': ArcheryGame, 'KeepUpGame': KeepUpGame, 'FillGame': FillGame, 'MathGame': MathGame,
            'SimonGame': SimonGame, 'BasketGame': BasketGame, 'SortGame': SortGame, 'WhackGame': WhackGame,
            'UnlockGame': UnlockGame, 'CountGame': CountGame, 'TypeGame': TypeGame, 'MatchGame': MatchGame,
            'DodgeGame': DodgeGame, 'BalanceGame': BalanceGame, 'FindGame': FindGame, 'MazeGame': MazeGame,
            'StackGame': StackGame, 'JumpGame': JumpGame, 'GolfGame': GolfGame
        };

        const safeParse = (val, defaultVal) => {
            if (typeof val === 'string') {
                try {
                    return JSON.parse(val);
                } catch (e) {
                    console.warn("[ChallengeManager] JSON Parse Error:", e, val);
                    return defaultVal;
                }
            }
            return val || defaultVal;
        };

        return defs.map(d => {
            let config = safeParse(d.config, {});
            let mode = d.mode;

            // EMERGENCY OVERRIDE: Fix for Survival games ending early
            // Some clients/DB states might not have received the 'survival' flag or 'infinite' mode correctly.
            const forceSurvivalIds = [
                'c19_locksmith', 'c20_hacker', 'c16_eagle_eye', 'c08_orbital', 'c06_math_whiz', 'c01_tap_blitz',
                'c21_chrono', 'c23_perfect_fit'
            ];
            if (forceSurvivalIds.includes(d.id)) {
                config.survival = true;
                mode = 'infinite';
                console.log(`[ChallengeManager] Forcing SURVIVAL/INFINITE for ${d.id}`);
            }

            return {
                ...d,
                gameClass: classMap[d.game_class] || classMap['TapGame'], // Map string to Class
                rewards: safeParse(d.rewards, {}),
                goals: safeParse(d.goals, []),
                config: config,
                mode: mode,
                streakTarget: d.streak_target
            };
        });
    }

    async fetchProgress() {
        if (!this.engine.authManager || !this.engine.authManager.token) return;
        const baseUrl = this.engine.authManager.db.baseUrl;
        try {
            const res = await fetch(`${baseUrl}/challenge/list`, {
                headers: { 'Authorization': `Bearer ${this.engine.authManager.token}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log("[ChallengeManager] Fetched Remote Progress:", data);

                if (Array.isArray(data)) {
                    data.forEach(item => {
                        const cId = item.challenge_id || item.challengeId;
                        if (!cId) return;

                        const remoteStars = item.stars || 0;
                        const remoteScore = item.score || 0;

                        // PURE SERVER SOURCE OF TRUTH - No merging with local state
                        this.progress[cId] = {
                            stars: remoteStars,
                            bestScore: remoteScore
                        };
                    });
                    console.log("[ChallengeManager] Sync Complete. Progress:", this.progress);
                }
            }
        } catch (e) {
            console.error("Failed to sync challenges", e);
        }
    }

    getStarCount(id) {
        const data = this.progress[id];
        if (!data) return 0;
        if (typeof data === 'number') return data;
        return data.stars || 0;
    }

    getBestScore(id) {
        const data = this.progress[id];
        if (!data) return 0;
        if (typeof data === 'number') return 0; // Legacy data only has stars
        return data.bestScore || 0;
    }

    async completeChallenge(challengeId, resultScore, timeTaken, won, roundsWon = 0) {
        const challenge = this.challenges.find(c => c.id === challengeId);
        if (!challenge) return;

        // Calculate Stats
        let starsEarned = 0;

        // Logic depends on challenge type
        if (challenge.goals && challenge.goals.length > 0) {
            // Check goals from 3 down to 1
            for (let i = challenge.goals.length - 1; i >= 0; i--) {
                const goal = challenge.goals[i];
                let met = true;
                if (goal.score && resultScore < goal.score) met = false;
                if (goal.timeLimit && timeTaken > goal.timeLimit) met = false;

                if (met) {
                    starsEarned = goal.stars;
                    break;
                }
            }
        } else {
            // Fallback
            starsEarned = won ? 3 : 0;
        }

        console.log(`[ChallengeManager] Completed ${challengeId}. Score: ${resultScore}, Stars: ${starsEarned}`);

        // Update Progress
        // Normalize Data - we enforce object structure now
        let currentData = this.progress[challengeId] || { stars: 0, bestScore: 0 };
        const oldStars = currentData.stars || 0;
        const oldScore = currentData.bestScore || 0;

        const newStars = Math.max(oldStars, starsEarned);
        const newBestScore = Math.max(oldScore, resultScore);

        // Optimistic update
        this.progress[challengeId] = {
            stars: newStars,
            bestScore: newBestScore
        };
        // Removed saveLocalProgress() as per user request to rely ONLY on DB

        // Sync with Server
        await this.submitResult(challengeId, newStars, newBestScore, challenge.rewards || { xp: 0, coins: 0 }, roundsWon);

        return { stars: starsEarned, newBest: starsEarned > oldStars, newHighScore: resultScore > oldScore };
    }

    async submitResult(challengeId, stars, score, rewards, roundsWon = 0) {
        if (!this.engine.authManager.token) return;
        const baseUrl = this.engine.authManager.db.baseUrl;

        try {
            const res = await fetch(`${baseUrl}/challenge/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.engine.authManager.token}`
                },
                body: JSON.stringify({
                    challengeId,
                    stars,
                    score, // Send best score
                    xpReward: rewards.xp,
                    coinReward: rewards.coins,
                    roundsWon: roundsWon
                })
            });

            const data = await res.json();
            if (data.success) {
                // Ensure we maintain object structure
                if (data.newStars !== undefined) {
                    this.progress[challengeId] = {
                        stars: data.newStars,
                        bestScore: data.newScore || score
                    };
                }
                // Always update local user balance if server returned it
                if (this.engine.authManager && this.engine.authManager.currentUser) {
                    if (data.newBalance !== undefined) this.engine.authManager.currentUser.coins = data.newBalance;
                    if (data.newXp !== undefined) this.engine.authManager.currentUser.xp = data.newXp;
                    if (data.newLevel !== undefined) this.engine.authManager.currentUser.level = data.newLevel;
                }
                this.engine.updateHUD(); // Refresh UI
            }
        } catch (e) {
            console.error("[ChallengeManager] Submit Result ERROR:", e);
        }
    }

    onUserChanged() {
        this.progress = {};
        if (this.engine.authManager.token) {
            this.fetchProgress(); // SYNC: Fetch server data on login
        }
    }
}
